import { createBrowserRouter, createRoutesFromElements, Navigate, Route} from "react-router";
import { lazy, Suspense } from "react";

import { HomeLayout } from "../pages/home/layout";
import { Providers } from "../providers";
import { AuthGuard } from "./guards/auth";
import { GuestGuard } from "./guards/guest";
import { AdminGuard } from "./guards/admin";
import { authLoader } from "./loaders/auth";
import { AuthLayout } from "../pages/auth/layout";


import { BuilderLayout } from "../pages/builder/layout";
import { AdminLayout } from "../pages/admin/layout";
import { DashboardLayout } from "../pages/dashboard/layout";

// Lazy load all page components
const HomePage = lazy(() => import("../pages/home/page").then(m => ({ default: m.HomePage })));
const TermsAndConditionsPage = lazy(() => import("../pages/home/terms-and-conditions/page"));
const PrivacyPolicyPage = lazy(() => import("../pages/home/privacy-policy/page"));
const BackupOtpPage = lazy(() => import("../pages/auth/backup-otp/page").then(m => ({ default: m.BackupOtpPage })));
const ForgotPasswordPage = lazy(() => import("../pages/auth/forgot-password/page").then(m => ({ default: m.ForgotPasswordPage })));
const LoginPage = lazy(() => import("../pages/auth/login/page").then(m => ({ default: m.LoginPage })));
const OAuthRedirectPage = lazy(() => import("../pages/auth/oauth-redirect/page").then(m => ({ default: m.OAuthRedirectPage })));
const RegisterPage = lazy(() => import("../pages/auth/register/page").then(m => ({ default: m.RegisterPage })));
const ResetPasswordPage = lazy(() => import("../pages/auth/reset-password/page").then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import("../pages/auth/verify-email/page").then(m => ({ default: m.VerifyEmailPage })));
const VerifyOtpPage = lazy(() => import("../pages/auth/verify-otp/page").then(m => ({ default: m.VerifyOtpPage })));
const BuilderPage = lazy(() => import("../pages/builder/page").then(m => ({ default: m.BuilderPage })));
const ResumesPage = lazy(() => import("../pages/dashboard/resumes/page").then(m => ({ default: m.ResumesPage })));
const SettingsPage = lazy(() => import("../pages/dashboard/settings/page").then(m => ({ default: m.SettingsPage })));
const UpgradePage = lazy(() => import("../pages/dashboard/upgrade").then(m => ({ default: m.UpgradePage })));
const ErrorPage = lazy(() => import("../pages/public/error").then(m => ({ default: m.ErrorPage })));
const PublicResumePage = lazy(() => import("../pages/public/page").then(m => ({ default: m.PublicResumePage })));
const AdminPage = lazy(() => import("../pages/admin/page").then(m => ({ default: m.AdminPage })));
const AnalysisLabPage = lazy(() => import("../pages/admin/analysis-lab/page").then(m => ({ default: m.AnalysisLabPage })));

import { builderLoader } from "../pages/builder/page";
import { publicLoader } from "../pages/public/page";


export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route errorElement={<Suspense fallback={null}><ErrorPage /></Suspense>}>
      <Route element={<HomeLayout />}>
        <Route path="/" element={<Suspense fallback={null}><HomePage /></Suspense>} />
        <Route path="/terms-and-conditions" element={<Suspense fallback={null}><TermsAndConditionsPage /></Suspense>} />
        <Route path="/privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
      </Route>

      <Route path="auth">
        <Route element={<AuthLayout />}>
          <Route element={<GuestGuard />}>
            <Route path="login" element={<Suspense fallback={null}><LoginPage /></Suspense>} />
            <Route path="register" element={<Suspense fallback={null}><RegisterPage /></Suspense>} />
          </Route>

          {/* Password Recovery */}
          <Route element={<GuestGuard />}>
            <Route path="forgot-password" element={<Suspense fallback={null}><ForgotPasswordPage /></Suspense>} />
            <Route path="reset-password" element={<Suspense fallback={null}><ResetPasswordPage /></Suspense>} />
          </Route>

          {/* Two-Factor Authentication */}
          <Route element={<GuestGuard />}>
            <Route path="verify-otp" element={<Suspense fallback={null}><VerifyOtpPage /></Suspense>} />
            <Route path="backup-otp" element={<Suspense fallback={null}><BackupOtpPage /></Suspense>} />
          </Route>

          {/* Email Verification */}
          <Route element={<AuthGuard />}>
            <Route path="verify-email" element={<Suspense fallback={null}><VerifyEmailPage /></Suspense>} />
          </Route>

          {/* OAuth Redirect Routes */}
          <Route element={<GuestGuard />}>
            <Route path="oauth/:provider" element={<Suspense fallback={null}><OAuthRedirectPage /></Suspense>} />
          </Route>

          {/* OAuth Callback */}
          <Route path="callback" loader={authLoader} element={<div />} />
        </Route>

        <Route index element={<Navigate replace to="/auth/login" />} />
      </Route>

      <Route path="dashboard">
        <Route element={<AuthGuard />}>
          <Route element={<DashboardLayout />}>
            <Route path="resumes" element={<Suspense fallback={null}><ResumesPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={null}><SettingsPage /></Suspense>} />
            <Route path="upgrade" element={<Suspense fallback={null}><UpgradePage /></Suspense>} />

            <Route index element={<Navigate replace to="/dashboard/resumes" />} />
          </Route>

          {/* Admin Routes */}
          <Route path="admin">
            <Route element={<AdminGuard />}>
              <Route element={<AdminLayout />}>
                <Route index element={<Suspense fallback={null}><AdminPage /></Suspense>} />
                <Route path="analysis-lab" element={<Suspense fallback={null}><AnalysisLabPage /></Suspense>} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="builder">
        <Route element={<AuthGuard allowAnonymous />}>
          <Route element={<BuilderLayout />}>
            <Route path=":id" loader={builderLoader} element={<Suspense fallback={null}><BuilderPage /></Suspense>} />

            <Route index element={<Navigate replace to="/dashboard/resumes" />} />
          </Route>
        </Route>
      </Route>

      {/* Public Routes */}
      <Route path=":username">
        <Route path=":slug" loader={publicLoader} element={<Suspense fallback={null}><PublicResumePage /></Suspense>} />
      </Route>
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
